import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, Trash2, Loader2, X, ChevronDown, AlertCircle, Pencil, CheckSquare } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TableRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/lib/errorMessage';
import { formatDateTime } from '@/lib/dateFormat';
import { Floating } from '@/components/ui/Floating';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { SelectField } from '@/components/ui/SelectField';

type Priority = 'low' | 'medium' | 'high' | 'urgent';
type Status = 'open' | 'in_progress' | 'done' | 'overdue';
type Recurring = 'none' | 'daily' | 'weekly' | 'monthly';

interface Task {
  id: string; title: string; description?: string;
  lead?: { id: string; name: string } | null;
  assignedTo?: { id: string; name: string } | null;
  priority: Priority; status: Status; recurring: Recurring;
  startAt?: string | null;
  endAt?: string | null;
  dueAt?: string | null;       // legacy alias for endAt
  createdAt: string;
}

interface User { id: string; name: string; }
interface Lead { id: string; name: string; }

const PRIORITY_COLORS: Record<Priority, string> = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  urgent: 'bg-red-50 text-red-700',
};

const STATUS_COLORS: Record<Status, string> = {
  open: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-indigo-50 text-indigo-700',
  done: 'bg-emerald-50 text-emerald-700',
  overdue: 'bg-red-50 text-red-700',
};

function isOverdue(task: Task) {
  const end = task.endAt || task.dueAt;
  return !!(end && new Date(end) < new Date() && task.status !== 'done');
}

// ── Task Form Drawer ────────────────────────────────────────────────────────────
function TaskDrawer({ task, users, leads, onClose, onSaved }: {
  task?: Task; users: User[]; leads: Lead[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!task;
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    leadId: task?.lead?.id ?? '',
    // New tasks default to the logged-in user; edit keeps whatever the task had.
    assignedToId: task?.assignedTo?.id ?? currentUser?.id ?? '',
    priority: task?.priority ?? 'medium',
    status: task?.status ?? 'open',
    recurring: task?.recurring ?? 'none',
    startAt: task?.startAt ? task.startAt.slice(0, 16) : '',
    endAt: (task?.endAt || task?.dueAt) ? (task.endAt || task.dueAt)!.slice(0, 16) : '',
  });
  const [saving, setSaving] = useState(false);
  const [leadSearch, setLeadSearch] = useState(task?.lead?.name ?? '');
  const [leadResults, setLeadResults] = useState<Lead[]>([]);
  const [showLeadDrop, setShowLeadDrop] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadWrapRef = useRef<HTMLDivElement>(null);

  // Fetch leads. Empty q fetches the most-recent 10 so the user sees
  // something the moment they focus the field (was previously empty).
  const fetchLeads = (q: string) => {
    setLeadError(null);
    setLeadLoading(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const url = q
          ? `/leads?search=${encodeURIComponent(q)}&limit=10`
          : `/leads?limit=10`;
        const { data } = await api.get(url);
        setLeadResults(Array.isArray(data) ? data : data.leads ?? data.data ?? []);
        setShowLeadDrop(true);
      } catch (err: any) {
        setLeadError(err?.response?.data?.message || 'Failed to load leads');
        setLeadResults([]);
        setShowLeadDrop(true);
      } finally {
        setLeadLoading(false);
      }
    }, 250);
  };

  const searchLeads = (q: string) => {
    setLeadSearch(q);
    if (!q) {
      setForm(p => ({ ...p, leadId: '' }));
    }
    fetchLeads(q);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      leadId: form.leadId || null,
      assignedToId: form.assignedToId || null,
      startAt: form.startAt || null,
      endAt: form.endAt || null,
    };
    try {
      if (isEdit) await api.patch(`/tasks/${task!.id}`, payload);
      else await api.post('/tasks', payload);
      toast('success', isEdit ? 'Task updated' : 'Task created');
      onSaved();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to save task'); }
    finally { setSaving(false); }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-slate-400';
  const selectCls = `${inputCls} bg-white`;
  const labelCls = 'block text-xs font-medium text-slate-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[480px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Task' : 'New Task'}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Title <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="Task title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Optional description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="relative" ref={leadWrapRef}>
            <label className={labelCls}>Lead (optional)</label>
            <input
              className={inputCls}
              placeholder="Search by name, phone, or email..."
              value={leadSearch}
              onChange={e => searchLeads(e.target.value)}
              onFocus={() => {
                // Always show dropdown on focus — fetch recent leads if empty.
                if (leadResults.length === 0 && !leadLoading) {
                  fetchLeads(leadSearch);
                } else {
                  setShowLeadDrop(true);
                }
              }}
              onBlur={() => {
                // Close after a tiny delay so click on a result registers first.
                setTimeout(() => setShowLeadDrop(false), 150);
              }}
            />
            <Floating
              open={showLeadDrop}
              anchorRef={leadWrapRef}
              onClose={() => setShowLeadDrop(false)}
              className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <button
                className="w-full px-3 py-2 text-sm text-left text-slate-500 hover:bg-gray-50 border-b border-gray-100"
                onMouseDown={() => { setForm(p => ({ ...p, leadId: '' })); setLeadSearch(''); setShowLeadDrop(false); }}>
                — No Lead
              </button>
              {leadLoading ? (
                <div className="px-3 py-3 text-xs text-slate-400 text-center">Loading…</div>
              ) : leadError ? (
                <div className="px-3 py-3 text-xs text-red-500 text-center">{leadError}</div>
              ) : leadResults.length === 0 ? (
                <div className="px-3 py-3 text-xs text-slate-400 text-center">
                  {leadSearch ? `No leads matching "${leadSearch}"` : 'No leads available'}
                </div>
              ) : (
                leadResults.map(l => (
                  <button
                    key={l.id}
                    className="w-full px-3 py-2 text-sm text-left text-slate-800 hover:bg-indigo-50"
                    onMouseDown={() => { setForm(p => ({ ...p, leadId: l.id })); setLeadSearch(l.name); setShowLeadDrop(false); }}>
                    {l.name}
                  </button>
                ))
              )}
            </Floating>
          </div>
          <div>
            <label className={labelCls}>Assigned To</label>
            <SelectField className={selectCls} value={form.assignedToId} onChange={e => setForm(p => ({ ...p, assignedToId: e.target.value }))}>
              <option value="">— Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </SelectField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Priority</label>
              <SelectField className={selectCls} value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value as Priority }))}>
                {['low', 'medium', 'high', 'urgent'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
              </SelectField>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <SelectField className={selectCls} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as Status }))}>
                {[['open', 'Open'], ['in_progress', 'In Progress'], ['done', 'Done'], ['overdue', 'Overdue']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </SelectField>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start Date</label>
              <DateTimeInput
                mode="datetime"
                value={form.startAt}
                onChange={v => setForm(p => ({ ...p, startAt: v }))}
              />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <DateTimeInput
                mode="datetime"
                value={form.endAt}
                min={form.startAt || undefined}
                onChange={v => setForm(p => ({ ...p, endAt: v }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Recurring</label>
              <SelectField className={selectCls} value={form.recurring} onChange={e => setForm(p => ({ ...p, recurring: e.target.value as Recurring }))}>
                {['none', 'daily', 'weekly', 'monthly'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
              </SelectField>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={save} disabled={saving || !form.title.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tasks Page ──────────────────────────────────────────────────────────────────
export function TasksPage() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; task?: Task }>({ open: false });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmTask, setConfirmTask] = useState<Task | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterAssignee) params.set('assignedToId', filterAssignee);
      const { data } = await api.get(`/tasks?${params}`);
      setTasks(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load tasks');
    } finally { setLoading(false); }
  }, [filterStatus, filterPriority, filterAssignee]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    // Role-scoped list — manager sees their team, counselor sees only self,
    // admin sees everyone except other admins. Same endpoint mobile uses.
    // Silent on failure — assignee dropdown just stays empty, no toast spam
    // on the task list page if /users/assignable is briefly unavailable.
    api.get('/users/assignable')
      .then(({ data }) => setUsers(Array.isArray(data) ? data : data.users ?? data.data ?? []))
      .catch((err) => console.warn('Failed to load assignable users:', err?.message));
  }, []);

  const handleDelete = (task: Task) => setConfirmTask(task);

  const handleConfirmDelete = async () => {
    if (!confirmTask) return;
    setConfirmLoading(true);
    setDeletingId(confirmTask.id);
    try { await api.delete(`/tasks/${confirmTask.id}`); fetchTasks(); toast('success', 'Task deleted'); setConfirmTask(null); }
    catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to delete'); }
    finally { setConfirmLoading(false); setDeletingId(null); }
  };

  const filtered = tasks.filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()));
  const { sorted, sort, onSort } = useSort(filtered);

  const fmt = (d: string) => formatDateTime(d);

  return (
    <DashboardLayout title="Tasks">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tasks</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage and track all tasks</p>
          </div>
          <button onClick={() => setDrawer({ open: true })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" /> Add Task
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..."
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-[#6366F1]" />
          </div>
          {[
            { label: 'Status', value: filterStatus, set: setFilterStatus, options: [['', 'All Status'], ['open', 'Open'], ['in_progress', 'In Progress'], ['done', 'Done'], ['overdue', 'Overdue']] },
            { label: 'Priority', value: filterPriority, set: setFilterPriority, options: [['', 'All Priority'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']] },
          ].map(({ label, value, set, options }) => (
            <SelectField key={label} value={value} onChange={e => set(e.target.value)}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#6366F1] bg-white">
              {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
          ))}
          <SelectField value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
            className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#6366F1] bg-white">
            <option value="">All Assignees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </SelectField>
          {(filterStatus || filterPriority || filterAssignee) && (
            <button onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterAssignee(''); }}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-500 hover:text-red-500 hover:border-red-200">
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <span className="text-xs text-slate-400">{loading ? 'Loading…' : `${filtered.length} task${filtered.length !== 1 ? 's' : ''}`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="Title" sortKey="title" sort={sort} onSort={onSort} />
                  <SortTh label="Lead" sortKey="lead.name" sort={sort} onSort={onSort} />
                  <SortTh label="Assigned To" sortKey="assignedTo.name" sort={sort} onSort={onSort} />
                  <SortTh label="Priority" sortKey="priority" sort={sort} onSort={onSort} />
                  <SortTh label="Start Date" sortKey="startAt" sort={sort} onSort={onSort} />
                  <SortTh label="End Date" sortKey="endAt" sort={sort} onSort={onSort} />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortTh label="Actions" sortable={false} />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  // Skeleton rows match the visible columns; gives the feel of
                  // "data is on its way" instead of a spinner with no shape.
                  Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={i} columns={8} />)
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4">
                      <EmptyState
                        icon={CheckSquare}
                        title="No tasks found"
                        description="Tasks created from leads or directly will appear here. Try clearing filters if you've narrowed the view."
                      />
                    </td>
                  </tr>
                ) : sorted.map(task => {
                  const overdue = isOverdue(task);
                  return (
                    <tr key={task.id} className={`hover:bg-gray-50/80 transition-colors ${overdue ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {overdue && <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                          <button onClick={() => setDrawer({ open: true, task })}
                            className={`font-medium hover:underline text-left ${overdue ? 'text-red-600' : 'text-[#6366F1]'}`}>
                            {task.title}
                          </button>
                        </div>
                        {task.description && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{task.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{task.lead?.name ?? <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{task.assignedTo?.name ?? <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${PRIORITY_COLORS[task.priority] ?? 'bg-gray-100 text-gray-600'}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {task.startAt ? <span>{fmt(task.startAt)}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {(task.endAt || task.dueAt) ? <span className={overdue ? 'text-red-600 font-medium' : ''}>{fmt((task.endAt || task.dueAt)!)}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[task.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => setDrawer({ open: true, task })} className="p-1.5 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {deletingId === task.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
                            <button onClick={() => handleDelete(task)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drawer.open && (
        <TaskDrawer task={drawer.task} users={users} leads={[]}
          onClose={() => setDrawer({ open: false })}
          onSaved={() => { setDrawer({ open: false }); fetchTasks(); }} />
      )}

      <ConfirmModal
        isOpen={!!confirmTask}
        title="Delete Task?"
        message={`Are you sure you want to delete "${confirmTask?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTask(null)}
        isLoading={confirmLoading}
      />
    </DashboardLayout>
  );
}
